import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import TableCard from "../../../components/ui-components/TableCard";
import FlexTable from "../../../components/ui-components/FlexTable";
import EmptyState from "../../../components/ui-components/EmptyState";
import Badge from "../../../components/ui-components/Badge";
import RightSheet from "../../../components/ui-components/RightSheet";
import { toast } from "../../../components/ui-components/Toast.jsx";
import { primaryButtonCls, secondaryButtonCls } from "../../../components/ui-components/buttonStyles.js";
import {
  listPreventiveActions,
  createPreventiveAction,
  updatePreventiveAction,
  deletePreventiveAction,
} from "../document/preventiveActionsApi.js";
import { listBudgets } from "../../Budgets/api.js";
import { fmtEur } from "../blocks/format.js";

// The 4 lifecycle statuses the backend accepts (empty defaults server-side to "proposed").
// toneStatus aliases an existing Badge tone key (see Badge.jsx STATUS_TONE) so the pill
// color is semantically right without teaching Badge new vocabulary.
const STATUS_META = {
  proposed: { label: "Proposed", toneStatus: "pending" },
  in_progress: { label: "In progress", toneStatus: "running" },
  done: { label: "Done", toneStatus: "success" },
  dropped: { label: "Dropped", toneStatus: "rejected" },
};
const STATUS_OPTIONS = Object.keys(STATUS_META);

const PORTFOLIO_WIDE = "__portfolio_wide__";

function emptyForm() {
  return {
    title: "",
    budgetId: PORTFOLIO_WIDE,
    owner: "",
    targetDate: "",
    status: "proposed",
    savingEur: "",
    deferDays: "",
  };
}

function formFromAction(action) {
  return {
    title: action.title || "",
    budgetId: action.budget_id != null ? String(action.budget_id) : PORTFOLIO_WIDE,
    owner: action.owner || "",
    targetDate: action.target_date ? action.target_date.slice(0, 10) : "",
    status: action.status || "proposed",
    savingEur: action.expected_saving_cents != null ? String(action.expected_saving_cents / 100) : "",
    deferDays: action.expected_breach_defer_days != null ? String(action.expected_breach_defer_days) : "",
  };
}

// Builds the create/update body from form state. target_date MUST be RFC3339 - the backend
// field is a Go *time.Time and a bare "YYYY-MM-DD" (what <input type=date> yields) fails to
// decode and 400s the whole request. Money is entered in euros and converted to cents. Blank
// optional fields are omitted (undefined), not sent as "" / null.
function bodyFromForm(form) {
  return {
    title: form.title.trim(),
    budget_id: form.budgetId === PORTFOLIO_WIDE ? undefined : Number(form.budgetId),
    owner: form.owner.trim() || undefined,
    target_date: form.targetDate ? new Date(`${form.targetDate}T00:00:00Z`).toISOString() : undefined,
    status: form.status,
    expected_saving_cents: form.savingEur !== "" ? Math.round(parseFloat(form.savingEur) * 100) : undefined,
    expected_breach_defer_days: form.deferDays !== "" ? parseInt(form.deferDays, 10) : undefined,
  };
}

function budgetLabel(action, budgetsById) {
  if (action.budget_id == null) return "Portfolio-wide";
  return budgetsById.get(action.budget_id)?.name || `Budget #${action.budget_id}`;
}

/**
 * PreventiveActionsPanel - the Actions tab of the report home, shown only for
 * budget_variance_risk reports: the authoring surface for mitigation tasks attached to the
 * report's variance/risk findings (the printed board report itself stays read-only - this is
 * where they're created/edited/deleted). Reuses the same TableCard + FlexTable list shell as
 * every other list page in the app.
 */
const PreventiveActionsPanel = () => {
  const [actions, setActions] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const reload = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [actionsData, budgetsRes] = await Promise.all([listPreventiveActions(), listBudgets({})]);
      setActions(Array.isArray(actionsData) ? actionsData : []);
      setBudgets(Array.isArray(budgetsRes?.budgets) ? budgetsRes.budgets : []);
    } catch (err) {
      setError(err.message || "Failed to load preventive actions");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const budgetsById = useMemo(() => new Map(budgets.map((b) => [b.id, b])), [budgets]);

  const openAdd = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm());
    setSheetOpen(true);
  }, []);

  const openEdit = useCallback((action) => {
    setEditingId(action.id);
    setForm(formFromAction(action));
    setSheetOpen(true);
  }, []);

  const closeSheet = useCallback(() => {
    if (saving) return;
    setSheetOpen(false);
  }, [saving]);

  const handleSave = useCallback(async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const body = bodyFromForm(form);
      if (editingId != null) {
        await updatePreventiveAction(editingId, body);
        toast.success("Action updated");
      } else {
        await createPreventiveAction(body);
        toast.success("Action added");
      }
      setSheetOpen(false);
      await reload();
    } catch (err) {
      toast.error(editingId != null ? "Failed to update action" : "Failed to add action");
    } finally {
      setSaving(false);
    }
  }, [editingId, form, reload]);

  const handleDelete = useCallback(
    async (action) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete "${action.title}"? This can't be undone.`)) return;
      setDeletingId(action.id);
      try {
        await deletePreventiveAction(action.id);
        toast.success("Action deleted");
        await reload();
      } catch (err) {
        toast.error("Failed to delete action");
      } finally {
        setDeletingId(null);
      }
    },
    [reload]
  );

  const columnDefs = useMemo(
    () => [
      {
        accessor: "title",
        Header: () => <span>Title</span>,
        Cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-medium text-text">{row.original.title}</div>
            {row.original.owner && <div className="truncate text-xs text-text-3">{row.original.owner}</div>}
          </div>
        ),
        width: 260,
        minWidth: 180,
      },
      {
        accessor: "budget_id",
        Header: () => <span>Budget</span>,
        Cell: ({ row }) => <span className="text-sm text-text-2">{budgetLabel(row.original, budgetsById)}</span>,
        width: 180,
        minWidth: 140,
      },
      {
        accessor: "target_date",
        Header: () => <span>Target date</span>,
        Cell: ({ value }) => <span className="text-sm text-text-2">{value ? value.slice(0, 10) : "—"}</span>,
        width: 130,
        minWidth: 110,
      },
      {
        accessor: "status",
        Header: () => <span>Status</span>,
        Cell: ({ value }) => {
          const meta = STATUS_META[value] || STATUS_META.proposed;
          return <Badge status={meta.toneStatus} label={meta.label} />;
        },
        width: 130,
        minWidth: 110,
      },
      {
        accessor: "expected_saving_cents",
        Header: () => <span>Expected saving</span>,
        align: "right",
        Cell: ({ value }) => <span className="text-sm text-text">{value != null ? fmtEur(value / 100) : "—"}</span>,
        width: 140,
        minWidth: 110,
      },
      {
        accessor: "expected_breach_defer_days",
        Header: () => <span>Defer days</span>,
        align: "right",
        Cell: ({ value }) => <span className="text-sm text-text-2">{value != null ? value : "—"}</span>,
        width: 110,
        minWidth: 90,
      },
      {
        id: "actions",
        align: "right",
        Header: () => null,
        Cell: ({ row }) => {
          const action = row.original;
          return (
            <div className="flex items-center justify-end gap-3" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(action);
                }}
              >
                Edit
              </button>
              <button
                type="button"
                className="text-xs text-danger hover:underline disabled:opacity-50"
                disabled={deletingId === action.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(action);
                }}
              >
                {deletingId === action.id ? "Deleting…" : "Delete"}
              </button>
            </div>
          );
        },
        width: 140,
        minWidth: 120,
      },
    ],
    [budgetsById, deletingId, handleDelete, openEdit]
  );

  const list = Array.isArray(actions) ? actions : [];
  const matchCount = list.length;
  const noun = matchCount === 1 ? "action" : "actions";
  const hint = isLoading ? "Loading…" : `${matchCount} ${noun}`;

  return (
    <>
      <TableCard
        title="Preventive actions"
        subtitle={hint}
        primaryAction={{ label: "Add action", onClick: openAdd, icon: icon({ name: "plus", style: "regular" }) }}
        isLoading={isLoading}
        error={error}
        errorMessage="Error loading preventive actions"
        onRetry={reload}
      >
        {list.length === 0 ? (
          <EmptyState
            icon={icon({ name: "shield-check", style: "light" })}
            title="No preventive actions yet"
            message="Add mitigation tasks for the risks and variances this report surfaces."
            action={{ label: "Add action", onClick: openAdd, icon: icon({ name: "plus", style: "regular" }) }}
          />
        ) : (
          <FlexTable columns={columnDefs} data={list} getRowId={(r) => r.id} />
        )}
      </TableCard>

      <RightSheet
        open={sheetOpen}
        onClose={closeSheet}
        title={editingId != null ? "Edit action" : "Add action"}
        subtitle="Mitigation task for a budget variance or risk finding."
        footer={
          <div className="flex items-center justify-end gap-3">
            <button type="button" className={secondaryButtonCls} onClick={closeSheet} disabled={saving}>
              Cancel
            </button>
            <button type="button" className={primaryButtonCls} onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="pa-title" className="text-xs font-medium text-text-2">
              Title
            </label>
            <input
              id="pa-title"
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label htmlFor="pa-budget" className="text-xs font-medium text-text-2">
              Budget
            </label>
            <select
              id="pa-budget"
              value={form.budgetId}
              onChange={(e) => setForm((f) => ({ ...f, budgetId: e.target.value }))}
              className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
            >
              <option value={PORTFOLIO_WIDE}>Portfolio-wide (no budget)</option>
              {budgets.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="pa-owner" className="text-xs font-medium text-text-2">
              Owner
            </label>
            <input
              id="pa-owner"
              type="text"
              value={form.owner}
              onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))}
              className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="pa-target-date" className="text-xs font-medium text-text-2">
                Target date
              </label>
              <input
                id="pa-target-date"
                type="date"
                value={form.targetDate}
                onChange={(e) => setForm((f) => ({ ...f, targetDate: e.target.value }))}
                className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="pa-status" className="text-xs font-medium text-text-2">
                Status
              </label>
              <select
                id="pa-status"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_META[s].label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="pa-saving" className="text-xs font-medium text-text-2">
                Expected saving (€)
              </label>
              <input
                id="pa-saving"
                type="number"
                step="0.01"
                min="0"
                value={form.savingEur}
                onChange={(e) => setForm((f) => ({ ...f, savingEur: e.target.value }))}
                className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="pa-defer-days" className="text-xs font-medium text-text-2">
                Breach-defer (days)
              </label>
              <input
                id="pa-defer-days"
                type="number"
                step="1"
                min="0"
                value={form.deferDays}
                onChange={(e) => setForm((f) => ({ ...f, deferDays: e.target.value }))}
                className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>
      </RightSheet>
    </>
  );
};

PreventiveActionsPanel.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  report: PropTypes.object,
};

export default PreventiveActionsPanel;
